import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

interface Props {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onAnswer: (answer: boolean) => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel,
  destructive,
  onAnswer,
}: Props) {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          {destructive && (
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
          )}
          <div className="flex-1">
            {title && <h2 className="text-lg font-semibold text-gray-900 mb-1">{title}</h2>}
            <p className="text-sm text-gray-600">{message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => onAnswer(false)}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            {cancelLabel ?? t('confirm.cancel')}
          </button>
          <button
            onClick={() => onAnswer(true)}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
              destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {confirmLabel ?? t('confirm.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
